import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { OnlineIndicator } from '@/components/profile/OnlineIndicator';
import type { ProfileListItem } from '@/lib/api/profiles';

type Props = {
  profile: ProfileListItem;
};

function isVerifiedTier(tier: string | null): boolean {
  return tier === 'verified' || tier === 'admin_verified';
}

export function ProfileCard({ profile }: Props) {
  const location = [profile.current_city, profile.current_country]
    .filter(Boolean)
    .join(', ');
  const headline = profile.headline ?? profile.role_title;
  const company = profile.company;
  const subtitle = [headline, company].filter(Boolean).join(' · ');
  const target = profile.handle ? `/profile/${profile.handle}` : '#';

  return (
    <Link
      to={target}
      className="group flex flex-col gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm transition-all duration-200 hover:border-gold/30 hover:shadow-[0_4px_24px_rgba(212,162,76,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
    >
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <Avatar
            name={profile.display_name}
            src={profile.avatar_url}
            size="lg"
          />
          <OnlineIndicator profileId={profile.id} lastSeenAt={profile.last_seen_at} mode="dot-only" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-base leading-snug text-navy group-hover:text-navy-deep [overflow-wrap:break-word]">
            {profile.display_name}
          </p>
          {subtitle && (
            <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-ink/70">{subtitle}</p>
          )}
          {location && (
            <p className="mt-1.5 flex items-start gap-1 text-xs text-muted">
              <MapPin className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="line-clamp-1">{location}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {profile.industry && (
          <Badge variant="gold">{profile.industry}</Badge>
        )}
        {profile.status === 'seeded' && (
          <Badge variant="seeded">Intro only</Badge>
        )}
        {(profile.status === 'claimed' || profile.status === 'active') &&
          !isVerifiedTier(profile.verification) && (
            <Badge variant="claimed">Member</Badge>
          )}
        {isVerifiedTier(profile.verification) && (
          <Badge variant="verified">Verified</Badge>
        )}
      </div>
    </Link>
  );
}
